<template>
  <div v-show="visible" class="task-panel-layer" data-qid="task-panel">
    <div class="task-panel-scrim" v-if="visible" @click="close"></div>
    <!-- Only built while open: every task row has its own panel -->
    <aside v-if="visible"
           class="task-panel"
           :class="{'task-panel--full': fullScreen}"
           role="dialog"
           aria-modal="true"
    >

      <!-- Header -->
      <header class="task-panel__head">
        <div class="task-panel__top">
          <div class="task-panel__crumb">
            <!-- Auditors who opened the documentation collection go back to the main task -->
            <span v-if="isClientView && canSeeMainTask"
                  class="task-panel__back-link"
                  @click="switchCurrentView('default')"
                  data-qid="task-panel-back-to-main"
            ><font-awesome-icon icon="chevron-left" class="mr-1"/>{{ $t('tasks.modal.back_to_main_task') }}</span>
            <template v-else>
              <span v-if="project.title">{{ project.title }}</span>
              <font-awesome-icon v-if="objectiveName" icon="chevron-right" class="mx-2"/>
              <span v-if="objectiveName">{{ objectiveName }}</span>
              <font-awesome-icon v-if="selectedTask.reference" icon="chevron-right" class="mx-2"/>
              <span v-if="selectedTask.reference">{{ selectedTask.reference }}</span>
            </template>
          </div>
          <span class="task-panel__position" v-if="position.total">{{ $t('tasks.v2.position', { index: position.index, total: position.total }) }}</span>
          <span class="task-panel__nav"
                :class="{'disabled': position.index <= 1}"
                @click="navigate(-1)"
                v-b-tooltip.hover
                :title="$t('tasks.v2.previous')"
                data-qid="task-panel-previous"
          ><font-awesome-icon icon="chevron-left"/></span>
          <span class="task-panel__nav"
                :class="{'disabled': position.total && position.index >= position.total}"
                @click="navigate(1)"
                v-b-tooltip.hover
                :title="$t('tasks.v2.next')"
                data-qid="task-panel-next"
          ><font-awesome-icon icon="chevron-right"/></span>
          <span class="task-panel__nav"
                @click="fullScreen = !fullScreen"
                v-b-tooltip.hover
                :title="$t('tasks.v2.full_screen')"
                data-qid="task-panel-full-screen"
          ><font-awesome-icon :icon="fullScreen ? 'compress' : 'expand'"/></span>
          <span v-if="!isClientView"
                class="task-panel__nav"
                :class="{'task-panel__nav--on': showHistory}"
                @click="toggleHistory"
                v-b-tooltip.hover
                :title="$t('tasks.v2.task_history')"
                data-qid="task-panel-history"
          ><font-awesome-icon icon="history"/></span>
          <span v-if="!isClientView && isOwner(selectedTask) && !projectLocked"
                class="task-panel__nav"
                @click="showEditQModal(selectedTask)"
                v-b-tooltip.hover
                :title="$t('tasks.v2.edit_question')"
                data-qid="task-panel-edit-question"
          ><font-awesome-icon icon="cog"/></span>
          <span class="task-panel__nav" @click="close" data-qid="task-panel-close"><font-awesome-icon icon="times"/></span>
        </div>

        <div class="task-panel__ref" v-if="selectedTask.reference">
          {{ selectedTask.reference }}<span v-if="isClientView"> · {{ $t('tasks.modal.data_collection_title') }}</span>
        </div>
        <div class="task-panel__title">
          <h2 data-qid="task-panel-title">{{ selectedTask.title || selectedTask.full_title }}</h2>
          <span class="v2-tag" :class="statusTag.cls">{{ statusTag.text }}</span>
        </div>

        <div class="task-panel__meta">
          <span>
            {{ isClientView ? $t('tasks.table.data_collection_assignee') : $t('tasks.table.task_assignee') }}
            <span class="task-panel__avatars" v-if="metaUsers.length">
              <Avatar v-for="user in metaUsers"
                      :key="'panel-user-' + user.id"
                      :object="user"
                      size="xs"
                      v-b-tooltip.hover
                      :title="user.name"
              ></Avatar>
            </span>
            <span v-else class="engagement-unassigned-pill">{{ $t('projects.engagement.filter.unassigned') }}</span>
            <span v-if="!isClientView && !readOnly"
                  class="v2-link cursor-pointer ml-1"
                  @click="addAssignees"
                  data-qid="task-panel-add-assignee"
            >+ {{ $t('tasks.v2.add') }}</span>
            <!-- The documentation collection can be handed to someone else, who can also be invited from the dialog -->
            <span v-if="isClientView && !projectLocked && selectedTask.status !== 'completed' && selectedTask.data_status !== 'completed'"
                  class="v2-link cursor-pointer ml-1"
                  @click="$refs.reassignDataUsers.show(selectedTask)"
                  data-qid="task-panel-reassign-data-users"
            ><font-awesome-icon icon="user-tag" class="mr-1"/>{{ $t('tasks.re_assign_button') }}</span>
          </span>
          <span v-if="dueDate">{{ isClientView ? $t('tasks.v2.deadline') : $t('tasks.v2.due') }} <b>{{ dueDate | formatDate('DD MMM', true) }}</b></span>
          <span v-if="selectedTask.data_collection_on && !isClientView">
            {{ $t('tasks.modal.data_collection_title') }}
            <b :class="selectedTask.data_status === 'completed' ? 'text-violet' : ''">{{ dataStatusText }}</b>
          </span>
        </div>

        <ProjectLockedNotice :project="project" class="project-locked--compact"/>

        <!-- Stages: each is its own page -->
        <nav v-if="!isClientView" class="task-panel__stages" :style="{ gridTemplateColumns: 'repeat(' + stages.length + ', 1fr)' }">
          <div v-for="(item, index) in stages"
               :key="'stage-' + item.key"
               class="task-stage"
               :class="['task-stage--' + item.state, {'task-stage--on': stage === item.key}]"
               @click="stage = item.key"
               :data-qid="'task-panel-stage-' + item.key"
          >
            <span class="task-stage__dot">
              <font-awesome-icon v-if="item.state === 'done'" icon="check"/>
              <span v-else>{{ index + 1 }}</span>
            </span>
            <span class="task-stage__text">
              <span class="task-stage__name">{{ item.name }}</span>
              <span class="task-stage__sub">{{ item.sub }}</span>
            </span>
            <span v-if="item.open" class="task-stage__open">{{ $tc('tasks.v2.open_fields', item.open, { count: item.open }) }}</span>
          </div>
        </nav>
      </header>

      <!-- Documentation collection: what is asked, the client's answer, files and tests; the chat on the right -->
      <div v-if="isClientView" class="task-panel__body">
        <div class="task-panel__main" data-qid="task-panel-main">
          <EngagementTaskClientView/>
        </div>
        <div class="task-panel__rail" :class="{'task-panel__rail--open': mobileChat}" data-qid="task-panel-rail">
          <span class="task-panel__rail-close" @click="mobileChat = false" data-qid="task-panel-chat-close"><font-awesome-icon icon="times"/></span>
          <EngagementTaskRail client/>
        </div>
      </div>

      <!-- Main task: the current stage on the left, the chat on the right -->
      <div v-else class="task-panel__body">
        <div class="task-panel__main" data-qid="task-panel-main">
          <b-alert v-if="error && error.length"
                   show
                   variant="danger"
                   dismissible
                   @dismissed="error = ''"
          >{{ error }}</b-alert>

          <div class="task-control" :class="{'task-control--oe': stage === 'oe'}" v-if="controlText">
            <div class="task-control__label">{{ stage === 'oe' ? $t('tasks.v2.control_oe') : $t('tasks.v2.control') }}</div>
            <div class="task-control__text"><RichTextView :value="controlText" :headings="false" compact></RichTextView></div>
          </div>

          <!-- D&I assessment: the client's documentation and the attachments first, then the auditor's fields -->
          <template v-if="stage === 'di'">
            <EngagementTaskDocuments/>
            <EngagementTaskTextCard v-if="enabled('add_control_description')"
                                    field="control_description"
                                    :title="$t('tasks.modal.control_description_label')"
                                    :required="selectedTask.add_control_description === 'Mandatory'"
                                    :placeholder="$t('tasks.modal.control_description_placeholder')"
                                    report
                                    ai
            />
            <EngagementTaskTextCard v-if="enabled('add_performed_text')"
                                    field="performed_text"
                                    :title="$t('tasks.modal.performed_test_label')"
                                    :required="selectedTask.add_performed_text === 'Mandatory'"
                                    :placeholder="$t('tasks.modal.performed_test_placeholder')"
                                    report
                                    ai
            />
          </template>

          <!-- OE testing: auditors can take samples while the task is still in D&I mode -->
          <template v-if="stage === 'oe'">
            <div v-if="samplesInDi"
                 class="task-next task-next--warning"
                 data-qid="task-panel-samples-in-di"
            >
              <font-awesome-icon icon="exclamation-triangle" class="mr-1"/>
              <span class="task-next__text">{{ $t('tasks.v2.samples_in_di') }}</span>
              <b-button v-if="canChangeTestPhase && !readOnly"
                        size="sm"
                        variant="success"
                        @click="setTestPhase('operating_effectiveness')"
                        data-qid="task-panel-move-to-oe"
              >{{ $t('tasks.v2.move_to_oe') }} <font-awesome-icon icon="chevron-right" class="ml-1"/></b-button>
            </div>
            <EngagementTaskTests/>
          </template>

          <EngagementTaskConclusion v-if="stage === 'conclusion'"/>
          <EngagementTaskQa v-if="stage === 'qa'"/>

          <!-- What comes next on this stage -->
          <div v-if="nextHint"
               class="task-next"
               :class="{'task-next--ready': nextHint.ready}"
               data-qid="task-panel-next-hint"
          >
            <font-awesome-icon :icon="nextHint.icon" class="mr-1"/>
            <span class="task-next__text">{{ nextHint.text }}</span>
            <b-button v-if="nextHint.phase !== undefined"
                      size="sm"
                      variant="success"
                      :disabled="!canChangeTestPhase"
                      @click="setTestPhase(nextHint.phase)"
                      data-qid="task-panel-start-oe"
            >{{ nextHint.action }} <font-awesome-icon icon="chevron-right" class="ml-1"/></b-button>
            <b-button v-else-if="nextHint.stage"
                      size="sm"
                      variant="secondary"
                      @click="stage = nextHint.stage"
            >{{ nextHint.action }} <font-awesome-icon icon="chevron-right" class="ml-1"/></b-button>
          </div>
        </div>

        <div class="task-panel__rail" :class="{'task-panel__rail--open': mobileChat}" data-qid="task-panel-rail">
          <span class="task-panel__rail-close" @click="mobileChat = false; showHistory = false" data-qid="task-panel-chat-close"><font-awesome-icon icon="times"/></span>
          <EngagementTaskRail :history="showHistory" @close-history="showHistory = false; mobileChat = false"/>
        </div>
      </div>

      <!-- Small screens: the chat opens from a button instead of sitting between the fields -->
      <span class="task-panel__chat-fab"
            :class="{'task-panel__chat-fab--raised': isClientView}"
            v-if="!mobileChat"
            @click="mobileChat = true; showHistory = false"
            v-b-tooltip.hover.left
            :title="$t('tasks.v2.open_chat')"
            data-qid="task-panel-chat-fab"
      >
        <font-awesome-icon icon="comment"/>
        <span v-if="previousComments.length" class="task-panel__chat-fab-count">{{ previousComments.length }}</span>
      </span>

      <!-- Documentation collection: deliver or re-open -->
      <footer v-if="isClientView" class="task-panel__actions" data-qid="task-panel-client-footer">
        <span class="task-panel__actions-hint">{{ clientFooterHint }}</span>
        <b-button variant="secondary" @click="close" data-qid="task-panel-client-close">{{ $t('system.close') }}</b-button>
        <b-button v-if="selectedTask.data_status === 'completed' && !readOnly"
                  variant="secondary"
                  :disabled="buttonDisabled"
                  @click="reOpenDataTask()"
                  data-qid="task-panel-client-reopen"
        ><font-awesome-icon icon="rotate-left" class="mr-1"/>{{ $t('tasks.v2.reopen') }}</b-button>
        <b-button v-if="selectedTask.data_status !== 'completed' && !readOnly && !isInOeTesting"
                  variant="success"
                  :disabled="buttonDisabled"
                  @click="completeDataTask()"
                  data-qid="task-panel-client-complete"
        ><font-awesome-icon icon="check" class="mr-1"/>{{ $t('tasks.v2.deliver') }}</b-button>
      </footer>

      <!-- The task modal's footer only holds the dialogs (QA, assignees) here -->
      <div class="d-none">
        <AssessmentGlobalSingleTaskModalFooter ref="footer"/>
        <DataCollectionReassignModal ref="reassignDataUsers" @saved="dataUsersReassigned"/>
      </div>
    </aside>
  </div>
</template>

<script>
import assessmentSingleTaskProxy from './assessmentSingleTaskProxy';
import Avatar from '@/components/Avatar.vue';
import ProjectLockedNotice from '@/components/ProjectLockedNotice.vue';
import AssessmentGlobalSingleTaskModalFooter from './AssessmentGlobalSingleTaskModalFooter.vue';
import EngagementTaskTextCard from './EngagementTaskTextCard.vue';
import EngagementTaskTests from './EngagementTaskTests.vue';
import EngagementTaskConclusion from './EngagementTaskConclusion.vue';
import EngagementTaskQa from './EngagementTaskQa.vue';
import EngagementTaskRail from './EngagementTaskRail.vue';
import EngagementTaskDocuments from './EngagementTaskDocuments.vue';
import RichTextView from '@/components/richText/RichTextView.vue';
import EngagementTaskClientView from './EngagementTaskClientView.vue';
import DataCollectionReassignModal from '@/components/tasks/DataCollectionReassignModal.vue';

// Every task row has its own panel: the history stays open when the user moves to the previous or next task
let historyOnNavigate = false;

// Engagement tasks open in this panel instead of the task modal. It uses the task modal's own
// fields and methods (via the proxy), so saving, conflicts, AI and tests behave the same.
export default {
  name: 'EngagementTaskPanel',
  mixins: [assessmentSingleTaskProxy],
  inject: {
    taskListNav: { default: null }
  },
  components: {
    RichTextView,
    Avatar,
    ProjectLockedNotice,
    AssessmentGlobalSingleTaskModalFooter,
    EngagementTaskTextCard,
    EngagementTaskTests,
    EngagementTaskConclusion,
    EngagementTaskQa,
    EngagementTaskRail,
    EngagementTaskDocuments,
    EngagementTaskClientView,
    DataCollectionReassignModal
  },
  data() {
    return {
      visible: false,
      fullScreen: false,
      // Small screens: the chat is open as a sheet over the task
      mobileChat: false,
      // The rail shows the task's history instead of the chat
      showHistory: false,
      stage: 'di',
      stageChosen: false
    };
  },
  computed: {
    isClientView() {
      return this.currentView === 'data_collection';
    },
    project() {
      return (this.selectedTask && this.selectedTask.project) || {};
    },
    objectiveName() {
      return this.selectedTask && this.selectedTask.control_objective ? this.selectedTask.control_objective.name : '';
    },
    position() {
      return this.taskListNav && this.selectedTask && this.selectedTask.id
        ? this.taskListNav.taskPosition(this.selectedTask.id)
        : { index: 0, total: 0 };
    },
    hasOe() {
      return this.usesOeTesting;
    },
    concludedTests() {
      return _.filter(this.tests || [], test => test.status === 'passed' || test.status === 'failed').length;
    },
    oeComplete() {
      return !this.hasOe || (this.isInOeTesting && (this.tests || []).length > 0 && this.concludedTests === this.tests.length);
    },
    // Samples are taken, but the task hasn't moved on from D&I to OE testing yet
    samplesInDi() {
      return this.hasOe && !this.isInOeTesting && (this.tests || []).length > 0 && this.selectedTask.status !== 'completed';
    },
    conclusionSaved() {
      return !!(this.selectedTask && this.selectedTask.remarks);
    },
    requiredSignOffs() {
      return Math.max(1, parseInt(this.project.required_sign_offs, 10) || 0);
    },
    stages() {
      const task = this.selectedTask || {};
      const completed = task.status === 'completed';
      const diDone = completed || this.isInOeTesting;
      let stages = [{
        key: 'di',
        name: this.$t('tasks.v2.stage_di'),
        state: diDone ? 'done' : 'now',
        sub: diDone ? this.$t('tasks.v2.done') : this.$t('system.in_progress'),
        open: this.openIn(['control_description', 'performed_test'])
      }];
      if (this.hasOe) {
        stages.push({
          key: 'oe',
          name: this.$t('tasks.v2.stage_oe'),
          state: completed || (this.isInOeTesting && this.oeComplete) ? 'done' : (this.isInOeTesting || this.samplesInDi ? 'now' : 'todo'),
          sub: this.$t('tasks.v2.tests_concluded', { done: this.concludedTests, total: (this.tests || []).length }),
          open: 0
        });
      }
      stages.push({
        key: 'conclusion',
        name: this.$t('tasks.v2.stage_conclusion'),
        state: completed ? 'done' : (this.conclusionSaved ? 'now' : 'todo'),
        sub: completed ? this.$t('tasks.v2.finalized') : (this.conclusionSaved ? this.$t('tasks.v2.saved') : '–'),
        open: this.openIn(['conclusion', 'response'])
      });
      stages.push({
        key: 'qa',
        name: this.$t('tasks.v2.stage_qa'),
        state: completed && (task.sign_off_count || 0) >= this.requiredSignOffs && task.qa_status !== 'rejected' ? 'done' : (completed ? 'now' : 'todo'),
        sub: this.$t('tasks.v2.sign_offs', { done: task.sign_off_count || 0, total: this.requiredSignOffs }),
        open: 0
      });
      return stages;
    },
    metaUsers() {
      return (this.isClientView ? this.selectedTask.data_users : this.selectedTask.users) || [];
    },
    dueDate() {
      return this.isClientView ? this.project.data_collection_end_date : this.project.end_date;
    },
    controlText() {
      const task = this.selectedTask || {};
      if (this.stage === 'di') {
        return task.description || '';
      }
      if (this.stage === 'oe') {
        return task.description_two || '';
      }
      return '';
    },
    // Auditors (project or task owners) can go back from the documentation collection to the main task
    canSeeMainTask() {
      return !!this.selectedTask && (this.isOwner(this.project) || this.isOwner(this.selectedTask));
    },
    clientFooterHint() {
      const task = this.selectedTask || {};
      if (this.projectLocked) {
        return this.$t('tasks.v2.client_project_locked_hint');
      }
      if (task.data_status === 'completed' || task.status === 'completed') {
        return this.$t('tasks.v2.client_delivered_hint');
      }
      return this.isInOeTesting ? this.$t('tasks.v2.client_oe_hint') : this.$t('tasks.v2.client_deliver_hint');
    },
    statusTag() {
      const task = this.selectedTask || {};
      if (this.isClientView) {
        if (task.data_status === 'completed' || task.status === 'completed') {
          return { cls: 'v2-tag--violet', text: this.$t('tasks.v2.delivered') };
        }
        return this.isInOeTesting
          ? { cls: 'v2-tag--blue', text: this.$t('tasks.v2.stage_oe_short') }
          : { cls: 'v2-tag--amber', text: this.$t('tasks.v2.to_deliver') };
      }
      if (task.status === 'completed') {
        if (task.qa_status === 'rejected') {
          return { cls: 'v2-tag--red', text: this.$t('system.rejected') };
        }
        return (task.sign_off_count || 0) >= this.requiredSignOffs
          ? { cls: 'v2-tag--teal', text: this.$t('system.signed_off') }
          : { cls: 'v2-tag--amber', text: this.$t('tasks.v2.awaiting_qa') };
      }
      if (this.isInOeTesting) {
        return { cls: 'v2-tag--blue', text: this.$t('tasks.test_phase.operating_effectiveness') };
      }
      return { cls: 'v2-tag--amber', text: this.$t('system.in_progress') };
    },
    dataStatusText() {
      const task = this.selectedTask || {};
      if (task.data_status === 'completed') {
        return this.$t('tasks.v2.delivered');
      }
      return this.$t('tasks.v2.waiting_on_client');
    },
    nextHint() {
      if (this.readOnly) {
        return null;
      }
      if (this.stage === 'di') {
        if (this.hasOe && !this.isInOeTesting) {
          return { icon: 'check', text: this.$t('tasks.v2.hint_start_oe'), phase: 'operating_effectiveness', action: this.$t('tasks.v2.start_oe') };
        }
        return { icon: 'check', text: this.$t('tasks.v2.hint_to_next'), stage: this.hasOe ? 'oe' : 'conclusion', action: this.hasOe ? this.$t('tasks.v2.stage_oe') : this.$t('tasks.v2.stage_conclusion') };
      }
      if (this.stage === 'oe') {
        // With samples the warning above them moves the task to OE testing
        if (this.samplesInDi) {
          return null;
        }
        if (!this.isInOeTesting) {
          return { icon: 'info-circle', text: this.$t('tasks.v2.oe_not_started'), phase: 'operating_effectiveness', action: this.$t('tasks.v2.start_oe') };
        }
        const open = (this.tests || []).length - this.concludedTests;
        return this.oeComplete
          ? { icon: 'check', text: this.$t('tasks.v2.hint_oe_done'), stage: 'conclusion', action: this.$t('tasks.v2.stage_conclusion'), ready: true }
          : { icon: 'flag', text: (this.tests || []).length ? this.$tc('tasks.v2.hint_oe_open', open, { count: open }) : this.$t('tasks.v2.hint_oe_no_tests') };
      }
      return null;
    }
  },
  methods: {
    show() {
      this.visible = true;
      this.stageChosen = false;
      this.showHistory = historyOnNavigate;
      this.mobileChat = historyOnNavigate;
      historyOnNavigate = false;
      this.pickStage();
      document.body.classList.add('task-panel-open');
    },
    // Main task assignees are added in the footer's dialog
    addAssignees() {
      if (this.$refs.footer) {
        this.$refs.footer.showAddAssigneesModal('main');
      }
    },
    enabled(setting) {
      return this.selectedTask[setting] === 'Optional' || this.selectedTask[setting] === 'Mandatory';
    },
    // On small screens the history opens as a sheet, like the chat
    toggleHistory() {
      this.showHistory = !this.showHistory;
      this.mobileChat = this.showHistory;
    },
    hide() {
      this.visible = false;
      this.mobileChat = false;
      this.showHistory = false;
      document.body.classList.remove('task-panel-open');
    },
    close() {
      // The task modal's own close resets the fields
      this.hideTaskModal();
    },
    // A task handed to others leaves the user's list, so the panel closes; otherwise it shows the new assignees
    dataUsersReassigned({ removedSelf }) {
      this.$store.commit('storeReloadTasks');
      if (removedSelf) {
        this.close();
        return;
      }
      this.loadTaskById(this.selectedTask.id, true);
    },
    navigate(direction) {
      if (!this.position.total) {
        return;
      }
      const next = this.position.index + direction;
      if (next < 1 || next > this.position.total) {
        return;
      }
      historyOnNavigate = this.showHistory;
      this.assessmentSingleTask.$emit('navigate', direction);
    },
    pickStage() {
      // Wait for the full task: the list's copy has no project, so OE testing isn't known yet
      if (this.stageChosen || !this.selectedTask || !this.selectedTask.id || !this.selectedTask.project || !this.selectedTask.project.project_type) {
        return;
      }
      const task = this.selectedTask;
      if (task.status === 'completed') {
        this.stage = 'qa';
      } else if (this.usesOeTesting && this.isInOeTesting) {
        this.stage = this.oeComplete && (this.tests || []).length ? 'conclusion' : 'oe';
      } else {
        this.stage = 'di';
      }
      this.stageChosen = true;
    },
    // The task modal opens empty fields for editing. The panel shows every field read-only until the
    // user edits it, so an untouched field (e.g. the notes on another tab) doesn't block Finalize task.
    closeUnchangedFields() {
      // Only the auditors' view; the host switches to it right after filling the form
      if (!this.selectedTask || !this.isOwner(this.selectedTask)) {
        return;
      }
      const task = this.selectedTask || {};
      const form = this.taskForm || {};
      const fields = {
        editModeControlDescription: 'control_description',
        editModePerformedTest: 'performed_text',
        editModeTestingNotes: 'testing_notes',
        editModeConclusion: 'remarks',
        editModeNotes: 'notes'
      };
      _.forEach(fields, (field, flag) => {
        if (this[flag] && (form[field] || '') === (task[field] || '')) {
          this[flag] = false;
        }
      });
      this.editModeResponse = false;
      this.editModeResponseDocMain = false;
    },
    // Number of fields in a stage that are in edit mode with text that differs from the saved text
    openIn(sections) {
      const task = this.selectedTask || {};
      const form = this.taskForm || {};
      const fields = {
        control_description: [this.editModeControlDescription, 'control_description'],
        performed_test: [this.editModePerformedTest, 'performed_text'],
        conclusion: [this.editModeConclusion, 'remarks']
      };
      return _.filter(sections, section => {
        const field = fields[section];
        return field && field[0] && (form[field[1]] || '') !== (task[field[1]] || '');
      }).length;
    }
  },
  mounted() {
    // Rendered at the end of the page so it sits above the app's top bar
    document.body.appendChild(this.$el);
    this.assessmentSingleTask.$on('populated', this.closeUnchangedFields);
    // Watched here: the task host's fields are only proxied once the component is created.
    // Start on the stage the task is in, once the full task has loaded.
    this.$watch(() => this.selectedTask && this.selectedTask.id, () => {
      this.stageChosen = false;
      this.pickStage();
    });
    this.$watch(() => [this.selectedTask && this.selectedTask.status, this.selectedTask && this.selectedTask.project && this.selectedTask.project.project_type].join('|'), () => this.pickStage());
    this.$watch(() => this.tests, () => this.pickStage());
  },
  beforeDestroy() {
    this.assessmentSingleTask.$off('populated', this.closeUnchangedFields);
    document.body.classList.remove('task-panel-open');
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el);
    }
  }
};
</script>
